import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { purple, black, transparentYellow, transparentGrey, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  yellowBox: {
    backgroundColor: transparentYellow,
    border: `1px solid ${transparentGrey}`,
    width: '100%',
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
  },
  contentBox: {
    width: '50%',
    margin: '20px 0',
    padding: '0 50px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '&:first-of-type': {
      borderRight: `2px solid ${purple}`,
    },
  },
  checkmark: {
    width: 78,
    height: 78,
    paddingRight: 25,
  },
  checkmarkCaption: {
    color: black,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  text: {
    marginTop: 30,
  },
  listHeader: {
    marginTop: 30,
    marginBottom: 10,
    fontWeight: 600,
  },
  listSection: {
    marginTop: 50,
    marginBottom: 50,
  },
  image: {
    paddingTop: 20,
    width: '100%',
  },
  bottomImage: {
    padding: 0,
    width: '80%',
    marginLeft: '10%',
  },
}));

const InterpretationPage07 = (): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <div className={classes.whiteBox}>
          <Typography variant="h5">Considerations for Physicians</Typography>
          <div className={classes.yellowBox}>
            <div className={classes.contentBox}>
              <img
                className={classes.checkmark}
                src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
              />
              <Typography className={classes.checkmarkCaption}>
                Was the prostate bed in the first bed position?
              </Typography>
            </div>
            <div className={classes.contentBox}>
              <img
                className={classes.checkmark}
                src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
              />
              <Typography className={classes.checkmarkCaption}>
                Was the scan time 5 minutes per bed position over the pelvis?
              </Typography>
            </div>
          </div>

          <Typography variant="body1" className={classes.text}>
            Recommended to scan for
            <b> 5 minutes per bed </b>
            position over pelvis (ie, pubic symphysis to iliac crest) to increase sensitivity of the detection of
            prostate cancer recurrence in sites typical for such recurrence
          </Typography>
          <Typography variant="body1" className={classes.text}>
            <i>
              Boxes represent the estimated area of image acquisition at a certain time point; rising window location is
              subject to change based on manufacturer specifications for the scanner.
            </i>
          </Typography>

          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.IMAGE_ACQ_TIME.path}`}
            alt={`${IMAGE_PATHS.IMAGE_ACQ_TIME.alt}`}
          />

          <Typography variant="body1" className={classes.text}>
            5 minutes per bed position is recommended, although this may be reduced to
            <b> 3 minutes per bed </b>
            position (if PET/CT scanner allows this adjustment and site is experienced with such image acquisition
            procedures)
          </Typography>
          <Typography variant="body1" className={classes.text}>
            <i>
              Boxes represent the estimated area of image acquisition at a certain time point; rising window location is
              subject to change based on manufacturer specifications for the scanner.
            </i>
          </Typography>

          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.IMAGE_ACQ_TIME2.path}`}
            alt={`${IMAGE_PATHS.IMAGE_ACQ_TIME2.alt}`}
          />

          <div className={classes.listSection}>
            <Typography variant="body1" className={classes.listHeader}>
              <b>Typical total scan time is between 20 to 30 minutes</b>
            </Typography>
            <ul className={classes.list}>
              <li>
                Actual scan time is typically dependent on scanner type, scan length, and time per bed position/ bed
                speed (scanners with continuous bed motion)
              </li>
              <li>
                <span>
                  Recommended to scan for
                  <b> 5 minutes per bed </b>
                  position over pelvis
                </span>
              </li>
              <ul className={classes.nestedList}>
                <li>
                  However, image acquisition time is scanner specific; start time of scan after injection (3 to 5
                  minutes, target 4 minutes) is more important
                </li>
                <li>
                  For remaining bed positions (ie, iliac crest to base of skull), 5 minutes per bed position is
                  recommended, although this may be reduced to 3 minutes per bed position (if PET/CT scanner allows this
                  adjustment and site is experienced with such image acquisition procedures)
                </li>
              </ul>
            </ul>
          </div>

          <Typography variant="h5">Image Reconstruction</Typography>
          <img
            className={classNames(classes.image, classes.bottomImage)}
            src={`${BASE_URL}${IMAGE_PATHS.AXUMIN_IMAGE_ACQ_CLIP.path}`}
            alt={`${IMAGE_PATHS.AXUMIN_IMAGE_ACQ_CLIP.alt}`}
          />
        </div>
      </div>
    </>
  );
};

export default InterpretationPage07;
